import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import { useAuth } from '@/contexts/auth';
import { useEncryption } from '@/contexts/encryption';
import { AppProviders } from '@/app/providers/AppProviders';
import { ProtectedLayout } from '@/components/layout/ProtectedLayout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AdminRoute } from '@/components/AdminRoute';
import { Layout } from '@/components/layout/Layout';
import { LazyRoute } from '@/components/lazy/LazyRoute';
import { GlobalErrorBoundary } from '@/components/ui/GlobalErrorBoundary';
import { SkipLinks } from '@/components/a11y/SkipLinks';
import { A11yAnnouncer } from '@/components/a11y/A11yAnnouncer';
import { InstallPWAPrompt } from '@/components/pwa/InstallPWAPrompt';

import { useRoutePreloader } from '@/hooks/useRoutePreloader';
import { 
  DashboardSkeleton, 
  TransactionsSkeleton, 
  CardsSkeleton, 
  AccountsSkeleton, 
  CategoriesSkeleton,
  BudgetsSkeleton,
  GoalsSkeleton,
  ReportsSkeleton,
  AgendaSkeleton,
  ReleasesSkeleton,
  AuthSkeleton,
  LandingSkeleton,
  BlogSkeleton
} from '@/components/skeletons';
import { useEffect } from 'react';
import { initializeSessionCleanup } from '@/utils/cleanup-sessions';
import { migrateToSecureStorage } from '@/lib/storage-migration';

// Lazy loaded pages
import { LazyReports } from '@/pages/lazy/LazyReports';
import { LazyCards } from '@/pages/lazy/LazyCards';
import { LazyAccounts } from '@/pages/lazy/LazyAccounts';
import { LazyCategories } from '@/pages/lazy/LazyCategories';
import { LazyBudgets } from '@/pages/lazy/LazyBudgets';
import { LazyGoals } from '@/pages/lazy/LazyGoals';
import { LazySettings } from '@/pages/lazy/LazySettings';
import { LazyHelpSupport } from '@/pages/lazy/LazyHelpSupport';
import { LazyDocumentation } from '@/pages/lazy/LazyDocumentation';
import { LazyDocumentationTopic } from '@/pages/lazy/LazyDocumentationTopic';
import { LazyReleasesV2 } from '@/pages/lazy/LazyReleasesV2';
import { LazyLanding } from '@/pages/lazy/LazyLanding';
import { LazyDashboard } from '@/pages/lazy/LazyDashboard';
import { LazyLogin } from '@/pages/lazy/LazyLogin';
import { LazySignUp } from '@/pages/lazy/LazySignUp';
import { LazyAgenda } from '@/pages/lazy/LazyAgenda';
import { LazyAdmin } from '@/pages/lazy/LazyAdmin';
import { LazyTransactions } from '@/pages/lazy/LazyTransactions';

// Lazy loaded non-critical pages
import { LazyResetPassword } from '@/pages/lazy/LazyResetPassword';
import { LazyPrivacyPolicy } from '@/pages/lazy/LazyPrivacyPolicy';
import { LazyTermsOfService } from '@/pages/lazy/LazyTermsOfService';
import { LazyPlanSelection } from '@/pages/lazy/LazyPlanSelection';
import { LazySubscriptionSuccess } from '@/pages/lazy/LazySubscriptionSuccess';
import { LazySubscriptionCanceled } from '@/pages/lazy/LazySubscriptionCanceled';

import { OfflineIndicator } from '@/components/ui/OfflineIndicator';

// Blog pages (lazy loaded)
import { lazy } from 'react';
const BlogHome = lazy(() => import('@/pages/blog/BlogHome'));
const BlogPost = lazy(() => import('@/pages/blog/BlogPost'));
const BlogCategory = lazy(() => import('@/pages/blog/BlogCategory'));
const BlogTag = lazy(() => import('@/pages/blog/BlogTag'));

// Loading screen during encryption health check
const EncryptionLoadingScreen = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-center space-y-6 max-w-sm p-8">
      <div className="relative mx-auto w-16 h-16">
        <div className="absolute inset-0 rounded-full border-4 border-muted"></div>
        <div className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
        <div className="absolute inset-3 rounded-full bg-primary/10 flex items-center justify-center">
          <svg 
            className="w-6 h-6 text-primary" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
            />
          </svg>
        </div>
      </div>
      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-foreground">Inicializando Segurança</h3>
        <p className="text-sm text-muted-foreground">
          Preparando sistema de criptografia para proteger seus dados...
        </p>
      </div>
    </div>
  </div>
);

// Main app content with routes
const AppContent = () => {
  useRoutePreloader();
  const location = useLocation();
  const { isCheckingEncryption, isEncryptionReady, encryptionError } = useEncryption();
  const { user } = useAuth();

  // Initialize storage migration and session cleanup - DEFERRED for performance
  useEffect(() => {
    const initializeStorage = () => {
      try {
        migrateToSecureStorage();
        initializeSessionCleanup();
      } catch (error) {
        console.error('[App] Storage initialization failed:', error);
      }
    };
    
    if ('requestIdleCallback' in window) {
      requestIdleCallback(initializeStorage, { timeout: 5000 });
    } else {
      setTimeout(initializeStorage, 100);
    }
  }, []);

  // Only show encryption loading/error for authenticated users
  if (user) {
    if (isCheckingEncryption) {
      return <EncryptionLoadingScreen />;
    }

    if (!isEncryptionReady && encryptionError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="text-center space-y-4 max-w-md">
            <h2 className="text-2xl font-bold text-destructive">Sistema Indisponível</h2>
            <p className="text-muted-foreground">{encryptionError}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              Tentar Novamente
            </button>
          </div>
        </div>
      );
    }
  }

  return (
    <div className="min-h-screen bg-background" id="main-content" role="main">
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          {/* Public routes */}
          <Route path="/" element={
            <LazyRoute fallback={<LandingSkeleton />}>
              <LazyLanding />
            </LazyRoute>
          } />
          <Route path="/login" element={
            <LazyRoute fallback={<AuthSkeleton />}>
              <LazyLogin />
            </LazyRoute>
          } />
          <Route path="/register" element={
            <LazyRoute fallback={<AuthSkeleton />}>
              <LazySignUp />
            </LazyRoute>
          } />
          <Route path="/reset-password" element={
            <LazyRoute fallback={<AuthSkeleton />}>
              <LazyResetPassword />
            </LazyRoute>
          } />
          <Route path="/privacy-policy" element={
            <LazyRoute><LazyPrivacyPolicy /></LazyRoute>
          } />
          <Route path="/politica-de-privacidade" element={
            <LazyRoute><LazyPrivacyPolicy /></LazyRoute>
          } />
          <Route path="/terms-of-service" element={
            <LazyRoute><LazyTermsOfService /></LazyRoute>
          } />
          <Route path="/termos-de-uso" element={
            <LazyRoute><LazyTermsOfService /></LazyRoute>
          } />
          
          {/* Blog routes */}
          <Route path="/blog" element={
            <LazyRoute fallback={<BlogSkeleton />} withTransition={false}>
              <BlogHome />
            </LazyRoute>
          } />
          <Route path="/blog/categoria/:slug" element={
            <LazyRoute fallback={<BlogSkeleton />} withTransition={false}>
              <BlogCategory />
            </LazyRoute>
          } />
          <Route path="/blog/tag/:slug" element={
            <LazyRoute fallback={<BlogSkeleton />} withTransition={false}>
              <BlogTag />
            </LazyRoute>
          } />
          <Route path="/blog/:slug" element={
            <LazyRoute fallback={<BlogSkeleton />} withTransition={false}>
              <BlogPost />
            </LazyRoute>
          } />
          
          {/* Onboarding routes */}
          <Route path="/onboarding/trial-selection" element={
            <LazyRoute><LazyPlanSelection /></LazyRoute>
          } />
          <Route path="/onboarding/trial-success" element={
            <LazyRoute><LazySubscriptionSuccess /></LazyRoute>
          } />
          <Route path="/onboarding/trial-canceled" element={
            <LazyRoute><LazySubscriptionCanceled /></LazyRoute>
          } />
          
          {/* 
            Protected routes — nested under ProtectedLayout.
            ProtectedLayout provides: ProtectedRoute + TrialGuard + Layout + <Outlet />.
            Each child only declares its path and lazy-loaded component.
          */}
          <Route element={<ProtectedLayout />}>
            <Route path="/dashboard" element={
              <LazyRoute fallback={<DashboardSkeleton />}><LazyDashboard /></LazyRoute>
            } />
            <Route path="/agenda" element={
              <LazyRoute fallback={<AgendaSkeleton />}><LazyAgenda /></LazyRoute>
            } />
            <Route path="/lancamentos" element={
              <LazyRoute fallback={<ReleasesSkeleton />}><LazyReleasesV2 /></LazyRoute>
            } />
            <Route path="/relatorios" element={
              <LazyRoute fallback={<ReportsSkeleton />}><LazyReports /></LazyRoute>
            } />
            <Route path="/transacoes" element={
              <LazyRoute fallback={<TransactionsSkeleton />}><LazyTransactions /></LazyRoute>
            } />
            <Route path="/cartoes" element={
              <LazyRoute fallback={<CardsSkeleton />}><LazyCards /></LazyRoute>
            } />
            <Route path="/contas" element={
              <LazyRoute fallback={<AccountsSkeleton />}><LazyAccounts /></LazyRoute>
            } />
            <Route path="/categorias" element={
              <LazyRoute fallback={<CategoriesSkeleton />}><LazyCategories /></LazyRoute>
            } />
            <Route path="/orcamentos" element={
              <LazyRoute fallback={<BudgetsSkeleton />}><LazyBudgets /></LazyRoute>
            } />
            <Route path="/metas" element={
              <LazyRoute fallback={<GoalsSkeleton />}><LazyGoals /></LazyRoute>
            } />
            <Route path="/configuracoes" element={
              <LazyRoute><LazySettings /></LazyRoute>
            } />
            <Route path="/ajuda-e-suporte" element={
              <LazyRoute><LazyHelpSupport /></LazyRoute>
            } />
            <Route path="/documentacao" element={
              <LazyRoute><LazyDocumentation /></LazyRoute>
            } />
            <Route path="/documentacao/:id" element={
              <LazyRoute><LazyDocumentationTopic /></LazyRoute>
            } />
            {/* Admin route — AdminRoute guard applied inside the shared layout */}
            <Route path="/admin" element={
              <AdminRoute>
                <LazyRoute><LazyAdmin /></LazyRoute>
              </AdminRoute>
            } />
          </Route>
          
          {/* Redirects from old English routes */}
          <Route path="/reports" element={<Navigate to="/relatorios" replace />} />
          <Route path="/transactions" element={<Navigate to="/transacoes" replace />} />
          <Route path="/cards" element={<Navigate to="/cartoes" replace />} />
          <Route path="/accounts" element={<Navigate to="/contas" replace />} />
          <Route path="/categories" element={<Navigate to="/categorias" replace />} />
          <Route path="/budgets" element={<Navigate to="/orcamentos" replace />} />
          <Route path="/goals" element={<Navigate to="/metas" replace />} />
          <Route path="/releases" element={<Navigate to="/lancamentos" replace />} />
          <Route path="/settings" element={<Navigate to="/configuracoes" replace />} />
        </Routes>
      </AnimatePresence>
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <GlobalErrorBoundary>
        <AppProviders>
          <SkipLinks />
          <A11yAnnouncer />
          <OfflineIndicator />
          <InstallPWAPrompt />
          <AppContent />
        </AppProviders>
      </GlobalErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
